import { Box } from '@mui/material'
import debounce from 'lodash/debounce'
import { forwardRef, useCallback, useRef, useState } from 'react'
import { SearchInputField, SearchInputFieldProps } from 'utoniq-ui'

import { ItemType } from '@/clients/utoniq-core/schema'
import { useForm, UseFormProps, useTranslation } from '@/core'
import { CellFrame, Organism, OrganismFrame } from '@/core/architecture'

import { FilterMenu } from '../../OwnedItems/template'
import type { Action } from '../viewModel'

type FilterType = 'general' | ItemType

export type SearchFormProps = {
  onFilter: Action['onFilter']
  onSearch: Action['onSearch']
}

export type SearchFormValues = {
  searchTerm: string
}

const schema: UseFormProps['schema'] = o => ({
  searchTerm: o.string().allow('').optional(),
})

const defaultValues: SearchFormValues = {
  searchTerm: '',
}

export const SearchForm: Organism<SearchFormProps> = ({
  onFilter,
  onSearch,
}) => {
  const { handleSubmit, register, setValue } = useForm<SearchFormValues>({
    schema,
    defaultValues,
  })

  const [currentFilter, setCurrnetFilter] = useState<FilterType>(
    'general' as FilterType
  )

  const handleChangeKeywordDebounce = useRef(
    debounce(
      handleSubmit(({ searchTerm }) => onSearch(searchTerm)),
      500
    )
  )

  const handleSelectFilter = useCallback(
    (value: string) => {
      onFilter(value as FilterType)
      setCurrnetFilter(value as FilterType)
    },
    [onFilter]
  )

  return (
    <OrganismFrame>
      <Box sx={{ px: 5 }}>
        <SearchInputCell
          {...register('searchTerm')}
          onChange={async event => {
            setValue('searchTerm', event.target.value)
            await handleChangeKeywordDebounce.current()
          }}
        />
      </Box>
      <FilterMenu onSelect={handleSelectFilter} currentFilter={currentFilter} />
    </OrganismFrame>
  )
}

const SearchInputCell = forwardRef(
  (props: Omit<SearchInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consoleItems')
    return (
      <CellFrame sx={{ width: '100%' }}>
        <SearchInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)
